import type { AnalyzeResult, SettingsAccount } from './settings-types.js';
import { ApiError } from './api.js';

/**
 * Learn layout on a Providers card (P2g). A generic source whose template is
 * missing fails its crawl with "no stored layout for <url> — use Learn layout
 * on this source" (server ingest/run.ts); the card answers that error with
 * the Add-stats learn-and-confirm flow, in place, so nobody re-pastes the
 * URL into Add stats and mints a second account for the same feeds.
 */

/** The crawl error a generic source raises when its template is missing. */
export const NO_LAYOUT_RE = /no stored layout/i;

/** Learn layout in place of the error text: only a generic source, only on that error. */
export function needsLearnLayout(a: Pick<SettingsAccount, 'provider' | 'lastError'>): boolean {
  return a.provider === 'generic' && NO_LAYOUT_RE.test(a.lastError ?? '');
}

/** The always-available action: any generic source, for an admin (the routes write a shared template). */
export function canLearnLayout(a: Pick<SettingsAccount, 'provider'>, isAdmin: boolean): boolean {
  return isAdmin && a.provider === 'generic';
}

export const layoutAnalyzePath = (id: string): string => `/api/accounts/${encodeURIComponent(id)}/layout/analyze`;
export const layoutStorePath = (id: string): string => `/api/accounts/${encodeURIComponent(id)}/layout`;

/** The review step's description - the Add-stats copy for the same modes. */
export function learnLayoutCopy(mode: AnalyzeResult['mode']): string {
  switch (mode) {
    case 'template':
      return 'A layout for this source is already learned — review and confirm.';
    case 'empty':
      return 'The URL answers but has no rows yet — there is nothing to learn from until data appears.';
    default:
      return 'Proposed column matching (one-time learn) — review and confirm.';
  }
}

/** Whether the review step can be saved: a mapping to save, rows to have learned it from, no problems. */
export function canSaveLayout(mode: AnalyzeResult['mode'] | undefined, hasMapping: boolean, problems: number): boolean {
  return mode !== undefined && mode !== 'empty' && hasMapping && problems === 0;
}

/** What the learn dialog prints when a request fails: the server's own error
 * text when it sent one, and a plain "did not answer" when the body was empty
 * or the request never reached it (2h item 4). */
export const SERVER_DID_NOT_ANSWER = 'The server did not answer — check it is running';
export function learnErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const text = typeof err.body?.error === 'string' ? err.body.error.trim() : '';
    return text || SERVER_DID_NOT_ANSWER;
  }
  if (err instanceof TypeError) return SERVER_DID_NOT_ANSWER; // fetch failed
  const msg = err instanceof Error ? err.message.trim() : '';
  return msg || SERVER_DID_NOT_ANSWER;
}

/** The card's line about a generic source's layout (owner rule 2026-09-12). */
export function layoutLine(layout: SettingsAccount['layout']): string | null {
  if (!layout) return null;
  if (!layout.confirmedAt) return 'layout not confirmed yet — Learn layout';
  const date = layout.confirmedAt.slice(0, 10);
  return layout.columns == null
    ? `layout confirmed ${date} (columns not snapshotted until the next pull)`
    : `layout confirmed ${date} on ${layout.columns} column${layout.columns === 1 ? '' : 's'}`;
}

